import Image from "components/Elements/Image/Image";
import Button from "components/Elements/Button/Button";
import { useContent } from "contexts/contentContext/contentContext";
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { throttle } from "lodash";

import styles from "../hero.module.scss";
import Icon from "components/Elements/Icon/Icon";
import useBunnerSection from "components/Pages/Advertising/hooks/useBunnerSection";

interface HeroBunnerSectionI {
  handleScrollToForm: () => void;
  isHeroBunnerScrolledToTop: boolean;
  setIsHeroBunnerScrolledToTop: Dispatch<SetStateAction<boolean>>;
  isFormBtnClick: boolean;
  setIsFormBtnClick: Dispatch<SetStateAction<boolean>>;
}

const HeroBunnerSection = ({
  handleScrollToForm,
  isHeroBunnerScrolledToTop,
  setIsHeroBunnerScrolledToTop,
  isFormBtnClick,
  setIsFormBtnClick,
}: HeroBunnerSectionI) => {
  const { getTextByKey, getDirection } = useContent();
  const direction = getDirection();
  const isAnimatingRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const scrollTimeoutRef = useRef(null);

  const {
    handleTabClick,
    sectionRef,
    isFullWidth,
    activeTab,
    setIsFullWidth,
    setActiveTab,
  } = useBunnerSection();

  // Memoize the benefits list to prevent recreation on each render
  const benefitsList = useMemo(
    () => [
      {
        text: getTextByKey("hero-bunner-benefit-list-item-1"),
      },
      {
        text: getTextByKey("hero-bunner-benefit-list-item-2"),
      },
      {
        text: getTextByKey("hero-bunner-benefit-list-item-3"),
      },
      {
        text: getTextByKey("hero-bunner-benefit-list-item-4"),
      },
    ],
    [getTextByKey]
  );

  // Memoize the tab click handler
  const handleTabClickMemo = useCallback(
    (tab: string) => {
      handleTabClick(tab);
    },
    [handleTabClick]
  );

  // Memoize the scroll to form handler
  const handleScrollToFormMemo = useCallback(() => {
    setIsFormBtnClick(true);
    handleScrollToForm();
    setTimeout(() => {
      setIsFormBtnClick(false);
    }, 1000);
  }, [handleScrollToForm, setIsFormBtnClick]);

  // Memoize the banner image name based on screen size
  const bannerImageName = useMemo(() => {
    if (window.innerWidth < 1400) {
      const size =
        window.innerWidth < 769
          ? "mob"
          : window.innerWidth < 1100
          ? "sm-tab"
          : "tab";
      return `advertising-page-main-banner-${direction}-${size}`;
    }
    return `advertising-page-main-banner-${direction}`;
  }, [direction]);

  // Memoize the image extension
  const bannerImageExt = useMemo(() => {
    return window.innerWidth < 1400 ? "jpg" : "webp";
  }, []);

  // Memoize the header image name
  const headerImageName = useMemo(() => {
    return window.innerWidth < 1200
      ? "advertising-page-header-mob-img"
      : "advertising-page-header-img";
  }, []);

  // Memoize the image classes
  const uploadImgClass = useMemo(() => {
    return `${styles.uploadImg} ${
      isFullWidth ? styles.uploadImgFull : styles.uploadImgPreview
    }`;
  }, [isFullWidth]);

  const fileInfoClass = useMemo(() => {
    return `${styles.uploadImg} ${
      isFullWidth ? styles.fileInfoFull : styles.fileInfoPreview
    }`;
  }, [isFullWidth]);

  // Memoize the content wrapper classes
  const contentWrapClass = useMemo(() => {
    return `${styles.heroBunnerTextContentWrap} ${
      isFullWidth ? styles.contentWrapFullWidth : styles.contentWrapPreview
    }`;
  }, [isFullWidth]);

  // Optimized scroll handler with throttling
  const handleScrollBunnerSection = useCallback(() => {
    const section = sectionRef.current;
    if (!section || isAnimatingRef.current) return;

    // Get current scroll position and section position
    const rect = section.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollingDown = scrollTop > lastScrollTopRef.current;
    lastScrollTopRef.current = scrollTop;

    // When scrolling down and section top is near viewport top
    if (rect.top <= 250 && window.innerWidth > 768 && scrollingDown) {
      isAnimatingRef.current = true;

      // Expand section to full width
      section.style.width = "100vw";
      section.style.borderRadius = "0px";
      setIsFullWidth(true);

      // Scroll to section top
      if (!isHeroBunnerScrolledToTop && !isFormBtnClick) {
        const sectionTop = section.offsetTop;

        // Use direct scrollTo with smooth behavior
        window.scrollTo({
          top: sectionTop,
          behavior: "smooth",
        });

        // Set flag after animation completes
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        scrollTimeoutRef.current = setTimeout(() => {
          setIsHeroBunnerScrolledToTop(true);
          isAnimatingRef.current = false;
        }, 500); // Match this with the transition duration
      } else {
        isAnimatingRef.current = false;
      }
    } else if (rect.top >= 0 && window.innerWidth > 768 && !scrollingDown) {
      // Reset styles without animation
      section.style.width = "";
      section.style.borderRadius = "";
      setIsFullWidth(false);
      setActiveTab("home");
      setIsHeroBunnerScrolledToTop(false);
    }
  }, [
    isFormBtnClick,
    isHeroBunnerScrolledToTop,
    sectionRef,
    setActiveTab,
    setIsFullWidth,
    setIsHeroBunnerScrolledToTop,
  ]);

  // Throttled scroll handler
  const throttledScrollHandler = useMemo(
    () => throttle(handleScrollBunnerSection, 100),
    [handleScrollBunnerSection]
  );

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    // Use a smoother transition for better performance
    section.style.transition = "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)";

    // Add hardware acceleration
    section.style.transform = "translateZ(0)";
    section.style.willChange = "width, border-radius";

    // Add event listeners
    window.addEventListener("scroll", throttledScrollHandler);

    // Clean up function
    return () => {
      window.removeEventListener("scroll", throttledScrollHandler);
      throttledScrollHandler.cancel();

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [throttledScrollHandler]);

  return (
    <section
      ref={sectionRef}
      className={styles.heroBunnerSection}
      aria-label={getTextByKey("hero-bunner-section-aria-label")}
      tabIndex={0}
    >
      <Image
        name={bannerImageName}
        ext={bannerImageExt}
        className={styles.bannerImg}
      />

      {isFullWidth && (
        <ul className={styles.heroBunnerSectionTabs} role="tablist">
          {["home", "download"].map((tab) => (
            <li
              key={tab}
              className={`${styles.heroBunnerSectionTab} ${
                activeTab === tab ? styles.active : ""
              }`}
              role="presentation"
            >
              <div
                role="tab"
                tabIndex={0}
                aria-selected={activeTab === tab}
                onClick={() => handleTabClickMemo(tab)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    handleTabClickMemo(tab);
                }}
              >
                {getTextByKey(`hero-bunner-section-${tab}-tab`)}
              </div>
            </li>
          ))}
        </ul>
      )}

      {!isFullWidth && (
        <Image
          id="advertising-page-header-img"
          name={headerImageName}
          ext="png"
          className={styles.headerImg}
        />
      )}
      {activeTab === "home" && (
        <Image
          name="advertising-page-upload-img"
          ext="png"
          className={uploadImgClass}
        />
      )}
      {activeTab === "download" && (
        <Image
          name="advertising-page-file-info"
          ext="png"
          className={fileInfoClass}
        />
      )}

      {isFullWidth && activeTab === "download" && (
        <Image
          name="advertising-page-footer-img"
          ext="png"
          className={styles.footerImg}
        />
      )}
      <div className={styles.heroBunnerContentWrap}>
        <div className={contentWrapClass}>
          <div className={styles.heroBunnerDescriptionWrap}>
            <p>{getTextByKey("hero-bunner-description-text-1")}</p>
            <p>{getTextByKey("hero-bunner-description-text-2")}</p>
          </div>
          <h2>{getTextByKey("hero-bunner-section-title")}</h2>
          <Button
            className="advertising-button"
            onClick={handleScrollToFormMemo}
          >
            {getTextByKey("hero-advertising-button-text")}
          </Button>
        </div>
        {(isFullWidth || window.innerWidth < 769) && (
          <ul className={styles.heroBunnerBenefitList}>
            {benefitsList.map((item, index) => (
              <li className={styles.heroBunnerBenefitListItem} key={index}>
                <Icon
                  name="check"
                  iconClass={styles.heroBunnerBenefitIcon}
                  gray
                />
                {item.text}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

// Memoize the entire component
export default React.memo(HeroBunnerSection);
